import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { TEXT_STYLES } from 'TEXT_STYLES'
import { toLineBreakFriendly } from 'helpers/strings'
import { formatImageResolution } from 'helpers/images'
import { redirectToKWS } from 'helpers/url'

import { Tooltip } from '@junglescout/edna'
import { AmazonLink } from 'ui_elements/AmazonLink/AmazonLink'
import { ImageStack } from 'ui_elements/ImageStack/ImageStack'
import { TruncatedTextWithTooltip } from 'ui_elements/TruncatedTextWithTooltip/TruncatedTextWithTooltip'
import { NakedButton } from 'ui_elements/NakedButton/NakedButton'

import { trackPendoEvent } from 'services/pendo'
import { DATABASE_TOOLTIPS } from 'constants/product_database/tooltips'
import { sendSegmentAsinInKSSearchedEvent } from 'helpers/segment'
import { NoDataTooltip } from 'ui_elements/NoDataTooltip/NoDataTooltip'

const TitleCellWrapper = styled.div`
  display: flex;
  width: 100%;

  &:hover {
    [class*='DropdownMenuWrapper'] {
      position: absolute;
      left: 50px;
    }
  }
`
const InCellButton = styled(NakedButton)`
  display: inline-block;
  color: ${props => props.theme.colors.primary} !important;
  padding: 0 2px;
  border-radius: 2px;
  margin: 0 -2px;
  transition: background-color 100ms ease;
  &:hover {
    background-color: ${props => props.theme.colors.primary200};
  }
`
const StyledImageStack = styled(ImageStack)`
  margin-right: 15px;
`
const AsinBrandWrapper = styled.div`
  ${TEXT_STYLES.BodyBlack};
  color: ${props => props.theme.colors.grey500};
  display: flex;
  align-items: center;
`
const StyledTruncatedText = styled(TruncatedTextWithTooltip)`
  margin-bottom: 5px;
`
const TitleSection = styled.div`
  width: ${props => (props.isProductVariant ? '193px' : '251px')};
  margin-right: 8px;
`
const Brand = styled.div`
  width: ${props => (props.leftOffset ? '95px' : '120px')};
  margin: 0;
  /* without this the tooltip for no data gets squished */
  ${props =>
    props.hasBrand &&
    `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 151px;
  `}
`
const StyledAmazonLink = styled(AmazonLink)`
  margin-left: auto;
  margin-top: 8px;
`
const Spacer = styled.div`
  width: 55px;
  min-width: 55px;
  max-width: 55px;
  content: '';
  flex-grow: 3;
`

export const ProductTitle = props => {
  const {
    product,
    appType,
    selectedCountry,
    productUrl,
    isProductVariant,
    leftOffset
  } = props

  const { name, imageUrl, brand, variantAsinsCount, product_url } = product

  const { t } = useTranslation(['product_database'])

  const formattedImage = useMemo(() => formatImageResolution(imageUrl), [
    imageUrl
  ])
  const productName = useMemo(() => toLineBreakFriendly(name), [name])

  const badgeNumber = variantAsinsCount || 0
  const showStack = !isProductVariant && badgeNumber > 0

  const redirectToKeywordScout = e => {
    e.stopPropagation()
    sendSegmentAsinInKSSearchedEvent(
      appType,
      product,
      selectedCountry,
      'Product Database'
    )

    return redirectToKWS(product)
  }

  const formatAsin = () => {
    return (
      <Tooltip
        side="bottom"
        size="160px"
        content={
          <div>
            {t(
              'product_database:DatabaseTable.asinTooltip',
              'Search on Keyword Scout'
            )}
          </div>
        }>
        <InCellButton onClick={redirectToKeywordScout}>
          {product.asin}
        </InCellButton>
      </Tooltip>
    )
  }

  return (
    <TitleCellWrapper>
      {leftOffset && <Spacer />}
      <StyledImageStack
        src={formattedImage}
        alt={name || 'n/a'}
        badgeNumber={badgeNumber}
        showStack={showStack}
      />
      <TitleSection isProductVariant={isProductVariant}>
        <StyledTruncatedText>{productName}</StyledTruncatedText>
        <AsinBrandWrapper>
          {formatAsin()}
          <span>&nbsp;|&nbsp;</span>
          <Brand hasBrand={!!brand} leftOffset={leftOffset}>
            {brand || (
              <NoDataTooltip
                content={DATABASE_TOOLTIPS.nullProductData.brand}
                size="medium"
                side="right"
              />
            )}
          </Brand>
        </AsinBrandWrapper>
      </TitleSection>
      <StyledAmazonLink
        product={product}
        url={productUrl || product_url}
        tooltip={t(
          'product_database:DatabaseTable.amazonLinkTooltip',
          'View on Amazon'
        )}
        onClick={() => {
          trackPendoEvent('Product Database - Product Open on Amazon')
        }}
      />
    </TitleCellWrapper>
  )
}

ProductTitle.defaultProps = {
  isProductVariant: false,
  leftOffset: false,
  productUrl: undefined,
  appType: 'default'
}

ProductTitle.propTypes = {
  appType: PropTypes.string,
  product: PropTypes.objectOf(PropTypes.any).isRequired,
  productUrl: PropTypes.string,
  leftOffset: PropTypes.bool,
  isProductVariant: PropTypes.bool,
  selectedCountry: PropTypes.string.isRequired
}
